import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useQueries,
} from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { useMemo } from 'react'

import { getDisbursementDetails, getDisbursements } from '@/api/disbursements'
import { getDateRangeFromPreset } from '@/lib/filter-helpers'
import { DisbursementRequestParams } from '@/types/disbursements'

export const GET_DISBURSEMENTS_QUERY = 'get-disbursements-query'

export const disbursementsQueryOptions = (params?: DisbursementRequestParams) =>
  infiniteQueryOptions({
    queryKey: [GET_DISBURSEMENTS_QUERY, params],
    queryFn: async ({ pageParam }) => {
      const { data } = await getDisbursements({
        size: 10,
        status: 'COMPLETED',
        page: pageParam,
        sort: 'time_created',
        direction: 'desc',
        ...params,
      })
      return data
    },
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage?.has_next_page ? lastPage?.page_number + 1 : undefined,
  })

export const GET_BULK_DISBURSEMENT_DETAIL_QUERY =
  'get-bulk-disbursement-detail-query'

export const useInfiniteDisbursementsWithDetails = () => {
  const searchParams = useSearch({
    from: '/_authenticated/account/_dashboard/',
  })
  const { fromString, toString } = useMemo(() => {
    return getDateRangeFromPreset({
      from: searchParams?.fromDate,
      to: searchParams?.toDate,
      presetId: searchParams?.datePreset,
    })
  }, [searchParams])

  const disbursementsQuery = useInfiniteQuery(
    disbursementsQueryOptions(
      fromString && toString
        ? {
            time_last_attempted_start: fromString,
            time_last_attempted_end: toString,
          }
        : undefined
    )
  )

  const disbursements =
    disbursementsQuery.data?.pages.flatMap(page => page.content) ?? []

  const detailsQueries = useQueries({
    queries: disbursements.map(disbursement => ({
      queryKey: [GET_BULK_DISBURSEMENT_DETAIL_QUERY, disbursement.id],
      queryFn: async () => {
        const { data } = await getDisbursementDetails(disbursement.payout.ref)
        return {
          details: data,
          ...disbursement,
        }
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: !!disbursement.payout.ref,
    })),
    combine: results => ({
      data: results.map(result => result.data),
      statuses: results.map(({ isPending }) => ({
        isPending,
      })),
    }),
  })

  return {
    disbursements,
    details: detailsQueries.data,
    disbursementsQuery,
    detailsQueries,
  }
}
